import Service, { inject } from "@ember/service"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type {
  RotationCalendar,
  RotationCalendarDateAnnotation,
} from "@shared/models/rotation-calendar"
import { objectId } from "@shared/util/object-id"
import type { Payload } from "cc-frontend/lib/actions/rotation-calendar/CHANGE_CALENDAR"
import { changeCalendar } from "cc-frontend/lib/actions/rotation-calendar/CHANGE_CALENDAR"
import { updateCourseToRotationCalendar } from "cc-frontend/utils/course/update-course-to-rotation-calendar"
import { filterCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { findDate } from "cc-frontend/utils/filter-dates/shared"
import { updatePlanbookToRotationCalendar } from "cc-frontend/utils/planbook/update-planbook-to-rotation-calendar"
import { createRotationCalendarDates } from "cc-frontend/utils/rotation-calendar/dates"
import { cloneDeep, flatMap, isNil, map, min, remove } from "lodash-es"
import type Dispatcher from "./dispatcher"

/*

Prepared action

- cause
  - Change Id - CHANGE_ID
  - Reset - RESET
  - Add Special Event Is Off  - ADD_IS_OFF
  - Add Special Event Is On - ADD_NOTE

- accepts:
  - new rotation calendar
  - new planbook calendar
  - new course calendar

- returns:
  - patch with changes to the two + n models
*/

export default class RotationEditor extends Service {
  @inject("dispatcher") declare dispatcher: Dispatcher

  // will also change the dates
  addRotation() {}

  deleteRotation() {}

  changeSchoolYearDates({
    dateString,
    startOrEnd,
    rotationCalendar,
    planbook,
    courses,
  }: {
    dateString: string
    startOrEnd: "START" | "END"
    rotationCalendar: RotationCalendar
    planbook: Planbook
    courses: Array<Course>
  }) {
    const updatedRotationCalendar = cloneDeep(rotationCalendar)

    if (startOrEnd === "START") {
      updatedRotationCalendar.attributes.semesters[0].startDate = dateString
      let effectiveDate = min(
        map(
          flatMap(courses, (course) => filterCourseDateCustom(course)),
          (date) => date.attributes.dateString
        )
      )
      rotationCalendar.attributes.datesLastChangedOn = effectiveDate || dateString
    } else if (startOrEnd === "END") {
      updatedRotationCalendar.attributes.semesters[0].endDate = dateString

      let minimumDateToChangeFrom = min([
        rotationCalendar.attributes.semesters[0].endDate,
        dateString,
      ])
      if (isNil(minimumDateToChangeFrom)) throw Error("No minimum date to change")
      rotationCalendar.attributes.datesLastChangedOn = minimumDateToChangeFrom
    }

    let updatedDates = createRotationCalendarDates(updatedRotationCalendar)
    updatedRotationCalendar.attributes.dates = updatedDates

    let formattedDate = dateFns.format(dateFns.parse(dateString), "ddd. MMM Do, YYYY")

    this.dispatcher.dispatchPreparedAction(
      changeCalendar({
        causeOfChange: "CHANGE_START_END_DATE",
        changeDescription: `Changed ${startOrEnd.toLowerCase()} of school year to ${formattedDate}`,
        rotationCalendar: {
          id: rotationCalendar.id,
          originalAttributes: rotationCalendar.attributes,
          updatedAttributes: updatedRotationCalendar.attributes,
        },
        courses: null,
        planbookId: planbook.id,
        planbook: null,
      })
    )
  }

  changeRotationIdOnDay({
    dateString,
    newRotationId,
    rotationCalendar,
    planbook,
    courses,
  }: {
    dateString: string
    newRotationId: string
    rotationCalendar: RotationCalendar
    planbook: Planbook
    courses: Array<Course>
  }) {
    const updatedRotationCalendar = cloneDeep(rotationCalendar)
    let newDateAnnotation: RotationCalendarDateAnnotation = {
      id: objectId(),
      type: "date-annotation-rotation-calendar-single",
      attributes: {
        changedRotationId: newRotationId,
        dateString: dateString,
        description: null,
        isOff: false,
        isPublic: false,
        rotationShould: "CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED",
        title: null,
      },
    }

    remove(updatedRotationCalendar.attributes.dateAnnotations, (annotation) => {
      return (
        annotation.type === "date-annotation-rotation-calendar-single" &&
        annotation.attributes.dateString === dateString &&
        (annotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_RESET" ||
          annotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED")
      )
    })

    let formattedDate = dateFns.format(dateFns.parse(dateString), "ddd. MMM Do, YYYY")

    rotationCalendar.attributes.datesLastChangedOn = dateString
    updatedRotationCalendar.attributes.dateAnnotations.push(newDateAnnotation)
    let updatedDates = createRotationCalendarDates(updatedRotationCalendar)
    updatedRotationCalendar.attributes.dates = updatedDates

    return this.generateAction({
      rotationCalendar: rotationCalendar,
      updatedRotationCalendar: updatedRotationCalendar,
      planbook: planbook,
      courses: courses,
      changeDescription: `Changed rotation on ${formattedDate}`,
      causeOfChange: "CHANGE_ROTATION_ID",
    })
  }

  resetRotation({
    dateString,
    rotationCalendar,
    planbook,
    courses,
  }: {
    dateString: string
    rotationCalendar: RotationCalendar
    planbook: Planbook
    courses: Array<Course>
  }) {
    const updatedRotationCalendar = cloneDeep(rotationCalendar)
    let newDateAnnotation: RotationCalendarDateAnnotation = {
      id: objectId(),
      type: "date-annotation-rotation-calendar-single",
      attributes: {
        changedRotationId: null,
        dateString: dateString,
        description: null,
        isOff: false,
        isPublic: false,
        rotationShould: "CHANGE_AND_CYCLE_SHOULD_RESET",
        title: null,
      },
    }
    remove(updatedRotationCalendar.attributes.dateAnnotations, (annotation) => {
      return (
        annotation.type === "date-annotation-rotation-calendar-single" &&
        annotation.attributes.dateString === dateString &&
        (annotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_RESET" ||
          annotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED")
      )
    })

    rotationCalendar.attributes.datesLastChangedOn = dateString
    updatedRotationCalendar.attributes.dateAnnotations.push(newDateAnnotation)
    let updatedDates = createRotationCalendarDates(updatedRotationCalendar)
    updatedRotationCalendar.attributes.dates = updatedDates

    let formattedDate = dateFns.format(dateFns.parse(dateString), "ddd. MMM Do, YYYY")

    return this.generateAction({
      rotationCalendar: rotationCalendar,
      updatedRotationCalendar: updatedRotationCalendar,
      planbook: planbook,
      courses: courses,
      changeDescription: `Reset rotation on ${formattedDate}`,
      causeOfChange: "RESET_ROTATION",
    })
  }

  skipRotation({
    dateString,
    rotationCalendar,
    planbook,
    courses,
  }: {
    dateString: string
    rotationCalendar: RotationCalendar
    planbook: Planbook
    courses: Array<Course>
  }) {
    const updatedRotationCalendar = cloneDeep(rotationCalendar)
    let rcDate = findDate(rotationCalendar.attributes.dates, dateString)
    if (rcDate === undefined) throw Error("Cannot skip a date which doesn't have a rotation date")
    let newDateAnnotation: RotationCalendarDateAnnotation = {
      id: objectId(),
      type: "date-annotation-rotation-calendar-single",
      attributes: {
        // keep the existing rotation id
        changedRotationId: rcDate.attributes.rotationId,
        dateString: dateString,
        description: null,
        isOff: false,
        isPublic: false,
        rotationShould: "CHANGE_AND_CYCLE_SHOULD_BUMP_OVER_DISRUPTION",
        title: null,
      },
    }
    remove(updatedRotationCalendar.attributes.dateAnnotations, (annotation) => {
      return (
        annotation.type === "date-annotation-rotation-calendar-single" &&
        annotation.attributes.dateString === dateString &&
        (annotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_RESET" ||
          annotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED")
      )
    })

    rotationCalendar.attributes.datesLastChangedOn = dateString
    updatedRotationCalendar.attributes.dateAnnotations.push(newDateAnnotation)
    let updatedDates = createRotationCalendarDates(updatedRotationCalendar)
    updatedRotationCalendar.attributes.dates = updatedDates

    let formattedDate = dateFns.format(dateFns.parse(dateString), "ddd. MMM Do, YYYY")

    return this.generateAction({
      rotationCalendar: rotationCalendar,
      updatedRotationCalendar: updatedRotationCalendar,
      planbook: planbook,
      courses: courses,
      changeDescription: `Skipped Rotation on ${formattedDate}`,
      causeOfChange: "SKIP_ROTATION_DAY",
    })
  }

  addSpecialEventIsOff({
    title,
    description,
    startDate,
    endDate,
    isPublic,
    rotationShould,
  }: {
    title: string
    description: string
    startDate: string
    endDate: string
    isPublic: boolean
    rotationShould:
      | "CHANGE_AND_CYCLE_SHOULD_BUMP_OVER_DISRUPTION"
      | "CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED"
  }) {}

  addSpecialEventIsOn({
    title,
    description,
    startDate,
    endDate,
    isPublic,
  }: {
    title: string
    description: string
    startDate: string
    endDate: string
    isPublic: boolean
  }) {}

  removeDateAnnotation({
    rotationCalendar,
    planbook,
    dateAnnotation,
    courses,
  }: {
    rotationCalendar: RotationCalendar
    planbook: Planbook
    dateAnnotation: RotationCalendarDateAnnotation
    courses: Array<Course>
  }) {
    const updatedRotationCalendar = cloneDeep(rotationCalendar)

    remove(
      updatedRotationCalendar.attributes.dateAnnotations,
      (annotation) => annotation.id === dateAnnotation.id
    )

    rotationCalendar.attributes.datesLastChangedOn = dateFns.format(new Date(), "YYYY-MM-DD")
    let updatedDates = createRotationCalendarDates(updatedRotationCalendar)
    updatedRotationCalendar.attributes.dates = updatedDates

    let dateDesription
    if (dateAnnotation.type === "date-annotation-rotation-calendar-single") {
      let formattedDate = dateFns.format(
        dateFns.parse(dateAnnotation.attributes.dateString),
        "ddd. MMM Do, YYYY"
      )
      dateDesription = `Removed event on ${formattedDate}`
    }
    if (dateAnnotation.type === "date-annotation-rotation-calendar-range") {
      let startDate = dateFns.format(
        dateFns.parse(dateAnnotation.attributes.startDate),
        "ddd. MMM Do, YYYY"
      )
      let endDate = dateFns.format(
        dateFns.parse(dateAnnotation.attributes.endDate),
        "ddd. MMM Do, YYYY"
      )
      dateDesription = `Removed event from ${startDate} to ${endDate}`
    }

    return this.generateAction({
      rotationCalendar: rotationCalendar,
      updatedRotationCalendar: updatedRotationCalendar,
      planbook: planbook,
      courses: courses,
      changeDescription: dateDesription,
      causeOfChange: "REMOVE_DATE_ANNOTATION",
    })
  }

  private generateAction({
    rotationCalendar,
    updatedRotationCalendar,
    planbook,
    courses,
    causeOfChange,
    changeDescription,
  }: {
    rotationCalendar: RotationCalendar
    updatedRotationCalendar: RotationCalendar
    planbook: Planbook
    courses: Array<Course>
    causeOfChange: Payload["causeOfChange"]
    changeDescription: string
  }) {
    let { dates, newCardStacks } = updatePlanbookToRotationCalendar(
      rotationCalendar,
      updatedRotationCalendar,
      planbook
    )

    const updatedPlanbook = cloneDeep(planbook)
    updatedPlanbook.attributes.calendar.dates = dates

    let updatedCourseCalendars = map(courses, (course) => {
      let { courseDates, units } = updateCourseToRotationCalendar(
        rotationCalendar,
        updatedRotationCalendar,
        planbook,
        updatedPlanbook,
        course
      )
      let newCalendar = cloneDeep(course.attributes.calendar)
      newCalendar.dates = courseDates
      newCalendar.units = units
      return {
        id: course.id,
        originalCalendar: course.attributes.calendar,
        updatedCalendar: newCalendar,
      }
    })

    // course changes
    this.dispatcher.dispatchPreparedAction(
      changeCalendar({
        changeDescription: changeDescription,
        causeOfChange: causeOfChange,
        rotationCalendar: {
          id: rotationCalendar.id,
          originalAttributes: rotationCalendar.attributes,
          updatedAttributes: updatedRotationCalendar.attributes,
        },
        courses: updatedCourseCalendars,
        planbookId: planbook.id,
        planbook: {
          id: planbook.id,
          originalCalendar: planbook.attributes.calendar,
          updatedCalendar: updatedPlanbook.attributes.calendar,
        },
      })
    )
  }
}
